import type {
  CallScript,
  CallScriptGraph,
  ApiResponse,
  CallScripts,
  Channel,
  Organisation,
} from "types";

import { AxiosError } from "axios";

import { getError, instance } from "./client";

export const NO_ACTIVE_SCRIPT_ERROR = "This channel does not have an active generation 4 scorecard";
const ACTIVE_SCRIPT_GENERATION = 4;

export const getOrganisations = async (): Promise<ApiResponse<Organisation[]>> => {
  try {
    const response = await instance.get<Organisation[]>("/workspace/");
    return [response.data, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const getChannels = async (organisationId?: number): Promise<ApiResponse<Channel[]>> => {
  try {
    if (!organisationId) {
      return [null, "No organisation ID provided and none stored"];
    }
    const response = await instance.get<Channel[]>(`/workspace/${organisationId}/project/`);
    return [response.data, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const getCallScripts = async (
  organisationId?: number,
  channelId?: number,
): Promise<ApiResponse<CallScripts>> => {
  try {
    if (!organisationId) {
      return [null, "No organisation found"];
    }

    if (!channelId) {
      return [null, "No channel found"];
    }

    const response = await instance.get<CallScripts>(
      `workspace/${organisationId}/project/${channelId}/callscript/`,
    );
    return [response.data, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const getCallScript = async (
  organisationId?: number,
  channelId?: number,
  callScriptId?: number,
): Promise<ApiResponse<CallScript>> => {
  try {
    if (!organisationId) {
      return [null, "No organisation found"];
    }

    if (!channelId) {
      return [null, "No channel found"];
    }

    if (!callScriptId) {
      return [null, "No call script found"];
    }

    const endpoint = `/workspace/${organisationId}/project/${channelId}/callscript/${callScriptId}/`;

    const response = await instance.get(endpoint);
    return [response.data, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const getCallScriptGraph = async (
  organisationId?: number,
  channelId?: number,
  callScriptId?: number,
): Promise<ApiResponse<CallScriptGraph>> => {
  try {
    if (!organisationId) {
      return [null, "No organisation found"];
    }

    if (!channelId) {
      return [null, "No channel found"];
    }

    if (!callScriptId) {
      return [null, "No call script found"];
    }

    const response = await instance.get(
      `/workspace/${organisationId}/project/${channelId}/callscript/${callScriptId}/graph/`,
    );
    return [response.data, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const getActiveCallScriptGraph = async (
  organisationId?: number,
  channelId?: number,
): Promise<ApiResponse<CallScriptGraph>> => {
  try {
    if (!organisationId) {
      return [null, "No organisation found"];
    }

    if (!channelId) {
      return [null, "No channel found"];
    }

    const response = await instance.get(
      `/workspace/${organisationId}/project/${channelId}/callscript/active/graph/`,
      { params: { generation: ACTIVE_SCRIPT_GENERATION } },
    );
    return [response.data, null];
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return [null, NO_ACTIVE_SCRIPT_ERROR];
    }
    return [null, getError(error)];
  }
};
